/**
 * Hook to get all cards for a specific set from the card index
 *
 * Filters the global card index by set code and returns MinimalCard data.
 * Uses O(n) filtering but runs only once per setCode change.
 */
import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { cardIndexAtom, cardIndexLoadingAtom, type MinimalCard } from '@/services/cardDatabase';

export interface UseSetCardsResult {
  /** Cards in this set sorted by collector number */
  cards: MinimalCard[];
  /** Total number of cards in the set */
  totalCount: number;
  /** Whether the card index is still loading */
  isLoading: boolean;
}

/**
 * Get all cards for a specific set code
 *
 * @param setCode - The set code (e.g., 'neo', 'znr'), case-insensitive
 * @returns Cards in the set sorted by collector number
 */
export function useSetCards(setCode: string | undefined): UseSetCardsResult {
  const allCards = useAtomValue(cardIndexAtom);
  const isLoading = useAtomValue(cardIndexLoadingAtom);

  const cards = useMemo(() => {
    if (!setCode || allCards.length === 0) {
      return [];
    }

    const normalizedCode = setCode.toLowerCase();

    // Filter cards by set code
    const setCards = allCards.filter((card) => card.set.toLowerCase() === normalizedCode);

    // Sort by collector number (numeric sort for numeric parts)
    return setCards.sort((a, b) => {
      // Try to parse as numbers first
      const aNum = parseInt(a.collector_number, 10);
      const bNum = parseInt(b.collector_number, 10);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }

      // Fall back to string comparison for non-numeric collector numbers
      return a.collector_number.localeCompare(b.collector_number, undefined, { numeric: true });
    });
  }, [allCards, setCode]);

  return {
    cards,
    totalCount: cards.length,
    isLoading,
  };
}
